import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

export type SyncPartnerResponse = {
  hubspot_company_id?: string;
  hubspot_partner_record_id?: string;
  created: boolean;
};

// POST /api/partners/:id/sync
//
// HubSpot push is synchronous (1-5s); the SPA shows a spinner and gets the
// returned ids for the post-sync linked-to toast. No optimistic UI — the
// user expects a delay, and the linked-id surface depends on the response.
//
// Replaces revalidatePath("/admin/partners/${id}") from
// /app/admin/partners/_actions.ts::syncPartner.
export function useSyncPartner() {
  const queryClient = useQueryClient();
  return useMutation<SyncPartnerResponse, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<SyncPartnerResponse>(`/partners/${id}/sync`, {
        method: "POST",
      }),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.detail(id) });
      // Phase 7 sub-bucket 10 — admin list + overview ripple. The list
      // surfaces the `hubspot_company_id` (used to render the HubSpot
      // chip); overview carries the resolved HubSpot deep-link URLs.
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.partners.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.partners.overview(id) });
    },
    onError: (err) => {
      toast.error(err.message ?? "HubSpot sync failed");
    },
  });
}
